import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { DemoSelector } from './DemoSelector';

/* ------------------------------------------------------------------ */
/*  Brand tokens — match live site + .cursorrules accent               */
/* ------------------------------------------------------------------ */

const BRAND = {
  navy: '#0B1F4A',
  blue: '#1A56DB',
  blueLight: '#60A5FA',
  green: '#22c55e',
  greenLight: '#4ade80',
} as const;

/* ------------------------------------------------------------------ */
/*  Phone mockup — CSS-only frame with dashboard content               */
/* ------------------------------------------------------------------ */

const PhoneStatusBar: React.FC = () => (
  <div className="flex items-center justify-between px-5 pt-3 pb-1 text-white text-[11px] font-semibold">
    <span>9:41</span>
    <div className="flex items-center gap-[3px]">
      <span className="inline-block w-[14px] h-[10px] rounded-[1px] border border-white/70" />
      <svg width="14" height="10" viewBox="0 0 14 10" fill="none" className="opacity-70">
        <path d="M1 9C4.5 5 9.5 5 13 9" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M3 7C5.5 4.5 8.5 4.5 11 7" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="7" cy="5" r="1" fill="white" />
      </svg>
      <svg width="22" height="10" viewBox="0 0 22 10" fill="none" className="opacity-70">
        <rect x="0.5" y="1" width="18" height="8" rx="2" stroke="white" strokeWidth="1" />
        <rect x="2" y="2.5" width="12" height="5" rx="1" fill="white" />
        <rect x="19.5" y="3.5" width="2" height="3" rx="0.5" fill="white" opacity="0.4" />
      </svg>
    </div>
  </div>
);

const DashboardMockup: React.FC = () => (
  <div
    className="bg-white rounded-[2rem] overflow-hidden w-[280px] sm:w-[300px] md:w-[320px]"
    style={{
      boxShadow:
        '0 25px 60px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.1)',
    }}
  >
    <div style={{ backgroundColor: BRAND.navy }} className="relative">
      <PhoneStatusBar />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[90px] h-[24px] bg-black rounded-b-2xl" />
    </div>

    <div
      className="px-4 pb-4 pt-2"
      style={{
        background: `linear-gradient(to bottom, ${BRAND.navy}, #0f2856)`,
      }}
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-white/70 text-[11px]">Buenos días 👋</p>
          <p className="text-white text-lg font-bold leading-tight">María</p>
          <p className="text-white/50 text-[10px]">Boutique Aurora</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
              <circle cx="12" cy="12" r="3" />
              <path d="M12 1v2m0 18v2M4.22 4.22l1.42 1.42m12.72 12.72 1.42 1.42M1 12h2m18 0h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
            </svg>
          </div>
          <div className="w-7 h-7 rounded-full bg-red-500 flex items-center justify-center relative">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
            <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-red-400 rounded-full border border-white" />
          </div>
        </div>
      </div>
    </div>

    <div className="px-3 -mt-1">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 grid grid-cols-3 divide-x divide-gray-100 text-center py-2.5">
        <div>
          <p style={{ color: BRAND.navy }} className="text-lg font-bold leading-none">
            12
          </p>
          <p className="text-gray-400 text-[9px] mt-0.5">Ventas hoy</p>
        </div>
        <div>
          <p style={{ color: BRAND.navy }} className="text-lg font-bold leading-none">
            $2,4M
          </p>
          <p className="text-gray-400 text-[9px] mt-0.5">Ingresos mes</p>
        </div>
        <div>
          <p style={{ color: BRAND.navy }} className="text-lg font-bold leading-none">
            312
          </p>
          <p className="text-gray-400 text-[9px] mt-0.5">Inventario</p>
        </div>
      </div>
    </div>

    <div className="px-3 pt-3 pb-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
          Alertas importantes
        </p>
        <p className="text-[10px] font-medium" style={{ color: BRAND.blue }}>
          Ver todas
        </p>
      </div>
      <div className="space-y-2">
        {[
          { icon: '✅', title: 'Cierre de caja completado', sub: 'Hoy · 7 ventas', amount: '$1.245.000' },
          { icon: '📦', title: 'Stock bajo', sub: 'Aretes plateados · 3 uds', amount: null },
          { icon: '🎂', title: 'Cumpleaños esta semana', sub: '4 clientes', amount: null },
          { icon: '💬', title: 'Andrea M. respondió', sub: 'WhatsApp · hace 5 min', amount: null },
        ].map((alert, i) => (
          <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg px-2.5 py-2">
            <span className="text-base shrink-0">{alert.icon}</span>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-semibold text-gray-800 truncate">{alert.title}</p>
              <p className="text-[9px] text-gray-400">{alert.sub}</p>
            </div>
            {alert.amount && (
              <p className="text-[11px] font-bold shrink-0" style={{ color: BRAND.green }}>
                {alert.amount}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  </div>
);

const FloatingBadge: React.FC<{
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}> = ({ children, className = '', style }) => (
  <div
    className={`absolute bg-white rounded-full shadow-lg px-3 py-1.5 text-xs font-semibold text-gray-800 whitespace-nowrap border border-gray-100 ${className}`}
    style={style}
  >
    {children}
  </div>
);

const MiniPhone: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => (
  <div className="flex flex-col items-center">
    <div className="bg-white rounded-2xl shadow-xl border border-gray-200 w-[220px] sm:w-[250px] overflow-hidden">
      <div className="bg-gray-900 text-white text-[10px] text-center py-1 font-medium">{title}</div>
      <div className="p-3 text-xs text-gray-600 min-h-[240px]">{children}</div>
    </div>
  </div>
);

const CheckIcon: React.FC = () => (
  <svg className="w-4 h-4 shrink-0" style={{ color: BRAND.blueLight }} fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

interface NativeHeroSectionProps {
  demoVideoUrl?: string;
}

export const NativeHeroSection: React.FC<NativeHeroSectionProps> = ({ demoVideoUrl }) => {
  const [showDemo, setShowDemo] = useState(false);

  const handleDemoClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setShowDemo(true);
  }, []);

  return (
    <>
      <section className="relative overflow-hidden" style={{ backgroundColor: BRAND.navy }}>
        <div
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            background: `radial-gradient(ellipse 80% 50% at 70% 40%, ${BRAND.blue}66 0%, transparent 70%)`,
          }}
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 sm:pt-28 pb-16 sm:pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-6 items-center">
            <div className="text-white max-w-xl">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-3 py-1.5 mb-6 border border-white/10">
                <span className="text-sm">🇨🇴 🇲🇽 🇦🇷</span>
                <span className="text-xs text-white/80 font-medium">
                  Hecho en Latinoamérica · para negocios como el tuyo
                </span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-[3.4rem] font-extrabold leading-[1.08] tracking-tight mb-5">
                Vende mejor,
                <br />
                <span
                  className="text-transparent bg-clip-text"
                  style={{
                    backgroundImage: `linear-gradient(to right, ${BRAND.blueLight}, #93c5fd)`,
                  }}
                >
                  crece más rápido
                </span>
                <span style={{ color: BRAND.blueLight }}>.</span>
              </h1>

              <p className="text-base sm:text-lg text-white/80 leading-relaxed mb-8 max-w-md">
                La app que organiza tus{' '}
                <strong className="text-white">ventas, clientes y pedidos</strong> para que te enfoques en lo que
                importa.
              </p>

              <div className="flex flex-wrap gap-3 mb-8">
                <Link
                  to="/registro"
                  className="inline-flex items-center justify-center text-white font-semibold px-6 py-3 rounded-xl transition-colors shadow-lg hover:opacity-95"
                  style={{
                    backgroundColor: BRAND.blue,
                    boxShadow: `0 10px 25px ${BRAND.blue}40`,
                  }}
                >
                  Comenzar prueba gratis
                </Link>
                <a
                  href="#hero-dia"
                  onClick={handleDemoClick}
                  className="inline-flex items-center justify-center border border-white/25 text-white font-semibold px-6 py-3 rounded-xl hover:bg-white/10 transition-colors"
                >
                  Ver cómo funciona · 2 min
                </a>
              </div>

              <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-white/70">
                <span className="inline-flex items-center gap-1.5">
                  <CheckIcon />
                  <strong className="text-white">30 días gratis</strong>, sin tarjeta
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <CheckIcon />
                  Listo en <strong className="text-white">10 minutos</strong>
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <CheckIcon />
                  Soporte humano <strong className="text-white">en español</strong>
                </span>
              </div>

              <p className="mt-4 text-xs text-white/50">
                Para <strong className="text-white/70">tiendas, salones, restaurantes</strong> y emprendedores · CO · MX ·
                AR
              </p>
            </div>

            <div className="relative flex justify-center lg:justify-end">
              <div className="relative">
                <DashboardMockup />

                <FloatingBadge className="hidden sm:flex -left-20 top-12 animate-[fadeSlideIn_0.6s_ease-out_0.3s_both]">
                  ✅ Fácil de usar
                </FloatingBadge>

                <FloatingBadge className="hidden sm:flex -left-24 bottom-32 animate-[fadeSlideIn_0.6s_ease-out_0.5s_both]">
                  🏪 ¡Mi tienda virtual en minutos!
                </FloatingBadge>

                <FloatingBadge
                  className="hidden sm:flex -right-14 top-6 animate-[fadeSlideIn_0.6s_ease-out_0.7s_both]"
                  style={{ background: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0' }}
                >
                  📈 +28% en ventas
                </FloatingBadge>

                <FloatingBadge className="hidden sm:flex -right-10 bottom-8 animate-[fadeSlideIn_0.6s_ease-out_0.9s_both]">
                  <span
                    className="w-5 h-5 rounded-full text-white text-[10px] font-bold flex items-center justify-center mr-1.5"
                    style={{ backgroundColor: BRAND.blue }}
                  >
                    M
                  </span>
                  &ldquo;Por fin entiendo mi negocio&rdquo;
                </FloatingBadge>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="inside scroll-mt-20 py-16 sm:py-20 bg-white" id="hero-dia">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs sm:text-sm font-semibold tracking-widest text-gray-500 uppercase mb-2">Negocios</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">Un día con Clientelo</h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              Sin tutoriales largos. Cada pantalla está pensada para que un dueño de negocio la entienda en 10 segundos.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6">
            <MiniPhone title="Panel principal">
              <div className="space-y-2">
                <div className="rounded-lg p-3 text-white" style={{ backgroundColor: BRAND.navy }}>
                  <p className="text-[10px] text-white/60">Buenos días 👋</p>
                  <p className="font-bold text-sm">Adam</p>
                  <p className="text-[9px] text-white/40">Boutique Aurora</p>
                </div>
                <div className="grid grid-cols-3 gap-1 text-center">
                  <div className="bg-gray-50 rounded-md p-2">
                    <p className="font-bold text-sm text-gray-800">0</p>
                    <p className="text-[8px] text-gray-400">Ventas hoy</p>
                  </div>
                  <div className="bg-gray-50 rounded-md p-2">
                    <p className="font-bold text-sm text-gray-800">$7,326</p>
                    <p className="text-[8px] text-gray-400">Ingresos mes</p>
                  </div>
                  <div className="bg-gray-50 rounded-md p-2">
                    <p className="font-bold text-sm text-gray-800">998</p>
                    <p className="text-[8px] text-gray-400">Inventario</p>
                  </div>
                </div>
                <div className="text-[9px] font-semibold text-gray-400 uppercase mt-2">Alertas importantes</div>
                <div className="space-y-1">
                  <div className="bg-gray-50 rounded px-2 py-1.5 text-[10px] text-gray-600">
                    🟢 nuevo_lead · Ana López completó un formulario
                  </div>
                  <div className="bg-gray-50 rounded px-2 py-1.5 text-[10px] text-gray-600">
                    🟢 nuevo_lead · Pedro Ruiz completó un formulario
                  </div>
                </div>
              </div>
            </MiniPhone>

            <MiniPhone title="Productos">
              <div className="space-y-2">
                <div className="bg-gray-100 rounded-md px-2 py-1.5 text-[10px] text-gray-400 flex items-center gap-1">
                  🔍 Buscar por nombre, SKU, categoría…
                </div>
                {[
                  { name: 'Cambio de Pantalla', price: '$0', cat: 'Arreglos' },
                  { name: 'Cargador Rápido...', price: '$79.000', cat: 'Accesorios' },
                  { name: 'Diagnóstico Técn...', price: '$0', cat: 'Arreglos' },
                  { name: 'Funda Silicona Pr...', price: '$35.000', cat: 'Accesorios' },
                  { name: 'iPhone 15 128GB', price: '$3.499.000', cat: 'Destacados' },
                ].map((p, i) => (
                  <div key={i} className="flex items-center gap-2 border-b border-gray-100 pb-1.5">
                    <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center text-[10px]">
                      📦
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-semibold text-gray-800 truncate">{p.name}</p>
                      <p className="text-[8px] text-gray-400">{p.cat}</p>
                    </div>
                    <p className="text-[10px] font-bold text-gray-700 shrink-0">{p.price}</p>
                  </div>
                ))}
              </div>
            </MiniPhone>

            <MiniPhone title="Factura">
              <div className="space-y-2">
                <div className="text-center">
                  <p className="font-bold text-sm text-gray-800">TI-828902691</p>
                  <span className="inline-block bg-yellow-100 text-yellow-700 text-[8px] font-semibold px-1.5 py-0.5 rounded">
                    Pendiente
                  </span>
                </div>
                <div className="border-t border-gray-100 pt-2 text-[10px] text-gray-600 space-y-0.5">
                  <p>Laura Gómez</p>
                  <p className="text-[9px] text-gray-400">laura@boutiqueaurora.co · Tel: 300 123 4567</p>
                </div>
                <div className="border-t border-gray-100 pt-2 text-[10px]">
                  <p className="text-gray-600">Yokohama Llanta Summer x1</p>
                  <div className="flex justify-between mt-1 text-gray-500">
                    <span>Subtotal</span>
                    <span>$692.752</span>
                  </div>
                  <div className="flex justify-between text-gray-500">
                    <span>IVA 19%</span>
                    <span>$131.622</span>
                  </div>
                  <div className="flex justify-between font-bold text-gray-900 text-xs mt-1 border-t border-gray-200 pt-1">
                    <span>TOTAL</span>
                    <span>$824.375</span>
                  </div>
                </div>
                <div className="space-y-1 pt-1">
                  <button
                    type="button"
                    className="w-full text-white text-[10px] font-semibold py-1.5 rounded-md"
                    style={{ backgroundColor: BRAND.blue }}
                  >
                    Ver factura
                  </button>
                  <button
                    type="button"
                    className="w-full text-white text-[10px] font-semibold py-1.5 rounded-md"
                    style={{ backgroundColor: BRAND.green }}
                  >
                    Programar entrega
                  </button>
                </div>
              </div>
            </MiniPhone>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Demo Selector Modal (Quick Demo + Guided Walkthrough) */}
      <DemoSelector isOpen={showDemo} onClose={() => setShowDemo(false)} videoUrl={demoVideoUrl} />
    </>
  );
};
